import React from "react";
import { Helmet } from "react-helmet-async";

/**
 * SEO component — sets <title>, meta description, Open Graph & Twitter cards
 * and optional JSON-LD structured data.
 */
export default function Seo({
  title,
  description,
  path = "/",
  image = "https://images.unsplash.com/photo-1568605114967-8130f3a36994?w=1200&q=80",
  jsonLd,
}) {
  const fullTitle = `${title} · Prime Build Design LTD`;
  const url = `https://primebuilddesign.co.uk${path}`;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content="Prime Build Design LTD" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {jsonLd && <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>}
    </Helmet>
  );
}

export const LOCAL_BUSINESS_JSONLD = {
  "@context": "https://schema.org",
  "@type": "GeneralContractor",
  name: "Prime Build Design LTD",
  description:
    "Premium domestic construction company in London. 10+ years of new builds, renovations, extensions and design-build projects across Wembley, Ealing, Richmond, Chiswick, Wimbledon, Hampstead and Croydon.",
  image: "https://primebuilddesign.co.uk/og-image.jpg",
  url: "https://primebuilddesign.co.uk",
  telephone: "+44 7481 626260",
  email: "primebuilddesignltd9@gmail.com",
  address: {
    "@type": "PostalAddress",
    streetAddress: "45 Bridge Road, Wembley Park",
    addressLocality: "London",
    postalCode: "HA9 9AG",
    addressCountry: "GB",
  },
  geo: {
    "@type": "GeoCoordinates",
    latitude: 51.5631,
    longitude: -0.2796,
  },
  areaServed: [
    { "@type": "Place", name: "Wembley Park" },
    { "@type": "Place", name: "Ealing" },
    { "@type": "Place", name: "Richmond" },
    { "@type": "Place", name: "Chiswick" },
    { "@type": "Place", name: "Wimbledon" },
    { "@type": "Place", name: "Hampstead" },
    { "@type": "Place", name: "Croydon" },
  ],
  priceRange: "£££",
  openingHoursSpecification: [
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      opens: "09:00",
      closes: "18:00",
    },
  ],
};

/**
 * Build a Schema.org BreadcrumbList from an array of {name, path} items.
 * Always prepends "Home → /".
 */
export function buildBreadcrumbs(items) {
  const base = "https://primebuilddesign.co.uk";
  const full = [{ name: "Home", path: "/" }, ...items];
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: full.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: `${base}${it.path}`,
    })),
  };
}
